import { MeetingDays } from "../dtos/classes.type";

export interface SessionConfig {
  enabled: boolean;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  totalClasses: number;
  holidayDates: string; // comma-separated "YYYY-MM-DD:YYYY-MM-DD" ranges
}

export type SessionStatus = "upcoming" | "going" | "past";

export interface ClassSessionInfo {
  status: SessionStatus;
  firstClassDate: Date;
  nextClassDate: Date | null;
  remainingClasses: number;
  proratedPrice: number;
  totalPrice: number;
}

/** Map meeting_days keys to JS day-of-week (0=Sun, 1=Mon, ..., 6=Sat) */
export const DAY_MAP: Record<keyof MeetingDays, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

/**
 * Get the current date/time, or override from URL param `session_now`.
 * Format: ?session_now=2026-04-15T14:00
 */
export function getSessionNow(): Date {
  const params = new URLSearchParams(window.location.search);
  const override = params.get("session_now");
  if (override) {
    const parsed = new Date(override);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return new Date();
}

/**
 * Read session config from #class-finder data attributes.
 */
export function getSessionConfig(): SessionConfig {
  const el = document.querySelector("#class-finder");
  return {
    enabled: el?.getAttribute("data-session-mode") === "yes",
    startDate: el?.getAttribute("data-session-start") || "",
    endDate: el?.getAttribute("data-session-end") || "",
    totalClasses:
      parseInt(
        el?.getAttribute("data-session-total-classes") || "12",
        10
      ) || 12,
    holidayDates: el?.getAttribute("data-session-holidays") || "",
  };
}

/** Format a Date as "YYYY-MM-DD" using local time. */
function formatDateKey(d: Date): string {
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

/**
 * Parse comma-separated holiday date ranges into a Set of "YYYY-MM-DD" strings.
 * Format: "2026-06-30:2026-07-04,2026-12-22:2026-12-26" or single dates "2026-07-04"
 */
function parseHolidayDates(raw: string): Set<string> {
  const holidays = new Set<string>();
  if (!raw.trim()) return holidays;

  raw.split(",").forEach((part) => {
    const trimmed = part.trim();
    if (trimmed.includes(":")) {
      const [startStr, endStr] = trimmed.split(":");
      const start = new Date(startStr + "T00:00:00");
      const end = new Date(endStr + "T00:00:00");
      const d = new Date(start);
      while (d <= end) {
        holidays.add(formatDateKey(d));
        d.setDate(d.getDate() + 1);
      }
    } else {
      holidays.add(trimmed);
    }
  });

  return holidays;
}

/**
 * Get active meeting day numbers (0-6) from MeetingDays object.
 */
export function getActiveDays(meetingDays: MeetingDays): number[] {
  return (Object.keys(DAY_MAP) as (keyof MeetingDays)[])
    .filter((key) => meetingDays[key])
    .map((key) => DAY_MAP[key]);
}

/**
 * Find the first occurrence of any meeting day on or after a given date.
 */
export function getFirstClassDate(
  sessionStart: Date,
  meetingDays: MeetingDays
): Date {
  const activeDays = getActiveDays(meetingDays);
  if (activeDays.length === 0) return sessionStart;

  const d = new Date(sessionStart);
  for (let i = 0; i < 7; i++) {
    if (activeDays.includes(d.getDay())) return new Date(d);
    d.setDate(d.getDate() + 1);
  }
  return sessionStart;
}

/**
 * Find the last occurrence of any meeting day on or before a given date.
 */
function getLastClassDate(
  sessionEnd: Date,
  meetingDays: MeetingDays
): Date {
  const activeDays = getActiveDays(meetingDays);
  if (activeDays.length === 0) return sessionEnd;

  const d = new Date(sessionEnd);
  for (let i = 0; i < 7; i++) {
    if (activeDays.includes(d.getDay())) return new Date(d);
    d.setDate(d.getDate() - 1);
  }
  return sessionEnd;
}

/**
 * Count remaining class meetings from `now` to session end.
 * A class on today counts if now is BEFORE the class start time.
 */
function countRemainingClasses(
  now: Date,
  sessionEnd: Date,
  meetingDays: MeetingDays,
  classStartTime: string, // "HH:MM" 24h format
  holidays: Set<string>
): number {
  const activeDays = getActiveDays(meetingDays);
  if (activeDays.length === 0) return 0;

  const [startHour, startMin] = classStartTime.split(":").map(Number);
  let count = 0;
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);

  const endDate = new Date(sessionEnd);
  endDate.setHours(23, 59, 59, 999);

  while (d <= endDate) {
    if (activeDays.includes(d.getDay()) && !holidays.has(formatDateKey(d))) {
      const classDateTime = new Date(d);
      classDateTime.setHours(startHour, startMin, 0, 0);

      if (now < classDateTime) {
        count++;
      }
    }
    d.setDate(d.getDate() + 1);
  }
  return count;
}

/**
 * Find the next meeting day on or after `now` within the session.
 */
function getNextClassDate(
  now: Date,
  sessionEnd: Date,
  meetingDays: MeetingDays,
  holidays: Set<string>
): Date | null {
  const activeDays = getActiveDays(meetingDays);
  if (activeDays.length === 0) return null;

  const d = new Date(now);
  d.setHours(0, 0, 0, 0);

  const endDate = new Date(sessionEnd);
  endDate.setHours(23, 59, 59, 999);

  while (d <= endDate) {
    if (activeDays.includes(d.getDay()) && !holidays.has(formatDateKey(d))) {
      return new Date(d);
    }
    d.setDate(d.getDate() + 1);
  }
  return null;
}

/**
 * Calculate session info for a given class.
 */
export function getClassSessionInfo(
  meetingDays: MeetingDays,
  classStartTime: string,
  classEndTime: string,
  tuitionFee: number,
  config: SessionConfig,
  now: Date
): ClassSessionInfo {
  const sessionStart = new Date(config.startDate + "T00:00:00");
  const sessionEnd = new Date(config.endDate + "T23:59:59");

  const firstClassDate = getFirstClassDate(sessionStart, meetingDays);
  const lastClassDate = getLastClassDate(sessionEnd, meetingDays);

  const [startH, startM] = classStartTime.split(":").map(Number);
  const firstClassStart = new Date(firstClassDate);
  firstClassStart.setHours(startH, startM, 0, 0);

  const [endH, endM] = classEndTime.split(":").map(Number);
  const lastClassEnd = new Date(lastClassDate);
  lastClassEnd.setHours(endH, endM, 0, 0);

  let status: SessionStatus;
  if (now < firstClassStart) {
    status = "upcoming";
  } else if (now >= lastClassEnd) {
    status = "past";
  } else {
    status = "going";
  }

  const holidays = parseHolidayDates(config.holidayDates);

  const remaining = countRemainingClasses(
    now,
    sessionEnd,
    meetingDays,
    classStartTime,
    holidays
  );
  const perClass = tuitionFee / config.totalClasses;
  const proratedPrice = Math.round(perClass * remaining * 100) / 100;

  const nextClassDate = status === "going"
    ? getNextClassDate(now, sessionEnd, meetingDays, holidays)
    : null;

  return {
    status,
    firstClassDate,
    nextClassDate,
    remainingClasses: remaining,
    proratedPrice,
    totalPrice: tuitionFee,
  };
}

/**
 * Format date as "MM/DD" style (e.g. "03/31").
 */
export function formatSessionDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}`;
}
